import { ReactNode, useEffect } from "react";

import { LoadingRedoUndoOutlined } from "@aircall/icons";
import { Box, Icon } from "@aircall/tractor";

import { INTERVAL_TIME, TRANSLATION_KEYS } from "./DataAccessLayer.constants";

import { CALL_TYPE } from "@/constants/call.decl";
import { formatSpeakerDisplay } from "@/helpers/speaker/speaker.helpers";
import { useAudioUrlQuery } from "@/hooks/useAudioUrlQuery/useAudioUrlQuery";
import { useCall } from "@/hooks/useCall/useCall";
import { useCallByIdQuery } from "@/hooks/useCall/useCallByIdQuery";
import { useGetCustomSummaryResults } from "@/hooks/useGetCustomSummaryResults";
import { useGetLine } from "@/hooks/useGetLine/useGetLine";
import { useGetPlaybookResults } from "@/hooks/useGetPlaybookResults";
import { useGetTagsAndNotes } from "@/hooks/useGetTagsAndNotes/useGetTagsAndNotes";
import { useTags } from "@/hooks/useTags/useTags";
import { useTranscriptionSettings } from "@/hooks/useTranscriptionSettings/useTranscriptionSettings";
import { AssetsCallType, getAssetType } from "@/states/call";
import { AssetType } from "@/states/path_param/path_param.decl";
import { usePathParams } from "@/states/path_param/path_params";
import { Navigate, useLocation } from "react-router";
import { DynamicLoader } from "../DynamicLoader/DynamicLoader";
import { ForbiddenErrorScreen } from "../ForbiddenErrorScreen/ForbinddenErrorScreen";
import { NotFoundErrorScreen } from "../NotFoundErrorScreen/NotFoundErrorScreen";

/**
 * The function responsible for validating the call type mentioned in the routing path "calls/${callId}/${callType}".
 * The function will validate the following:
 * 1) The type value is valid - "voicemail" | "recording"
 * 2) The type is correlate with the call type we are getting from the backend api.
 * @param routingCallType
 * @param callQuery
 */
const isTypeValid = (
  routingCallType: AssetType,
  serverCallType: AssetsCallType
): boolean => {
  // Verifying the type from the routing path
  const routingCallTypeValid =
    routingCallType === CALL_TYPE.Voicemail ||
    routingCallType === CALL_TYPE.Recording;

  // Validate the type from the server
  const serverCallTypeValid =
    serverCallType && getAssetType(serverCallType) === routingCallType;

  return routingCallTypeValid && serverCallTypeValid;
};

const isAudioForbidden = (message: string | undefined) =>
  // https://aircall-product.atlassian.net/wiki/spaces/7SE/pages/2751430665/Error+Management+at+Scale
  message?.includes("03");

export function DataAccessLayer({ children }: { children: ReactNode }) {
  const { callId, callType } = usePathParams();
  const location = useLocation();

  const { loading: isLoadingAudioUrl, error: audioUrlError } =
    useAudioUrlQuery();

  const callQuery = useCallByIdQuery(callId);
  const { call } = useCall();

  const { loading: isLoadingPlaybookResult } = useGetPlaybookResults(callId);
  const { loading: isLoadingCustomSummaryResult } =
    useGetCustomSummaryResults(callId);

  // Fetches the lineId so that we don't have to wait for the Call query to finish until we can start fetching GetLine, and GetLineTranscriptionsSettings.
  const { lineId, loading: isLoadingLine } = useGetLine();
  const { loading: isLoadingTranscriptionSettings } =
    useTranscriptionSettings(lineId);
  useGetTagsAndNotes({ callId });
  useTags();

  useEffect(() => {
    if (call?.speakers) {
      document.title = formatSpeakerDisplay([...call.speakers.values()]);
    }
  }, [call?.speakers]);

  // Tags and notes loading state is handled in the section itself.
  const isLoading =
    !call ||
    isLoadingAudioUrl ||
    isLoadingLine ||
    isLoadingTranscriptionSettings ||
    isLoadingPlaybookResult ||
    isLoadingCustomSummaryResult;

  if (isLoading) {
    return (
      <DynamicLoader
        translationKeys={TRANSLATION_KEYS}
        intervalTime={INTERVAL_TIME}
        h="100%"
        alignItems="center"
        justifyContent="center"
      >
        <Icon
          component={LoadingRedoUndoOutlined}
          color="primary-500"
          size={40}
          spin
        />
      </DynamicLoader>
    );
  }

  // handle 403 error from audioQuery
  if (isAudioForbidden(audioUrlError?.message)) {
    return <ForbiddenErrorScreen />;
  }

  const hasQueryError = !!callQuery.error || !!audioUrlError;
  const isCallTypeValid = isTypeValid(callType, call.type);

  if (hasQueryError || !isCallTypeValid) {
    // Temp code to be deleted in some weeks
    // Check if we have a mismatch: URL has "recording" but call type is "voicemail"
    const hasRecordingInUrl = location.pathname.includes("/recording");
    const isVoicemail = call?.type === "voicemail";

    if (hasRecordingInUrl && isVoicemail) {
      // Redirect to the correct URL by replacing "recording" with "voicemail"
      const correctedPath = location.pathname.replace(
        "/recording",
        "/voicemail"
      );
      return <Navigate to={correctedPath + location.search} replace />;
    }
    // end of temp code
    return <NotFoundErrorScreen />;
  }

  return (
    <Box data-test="data-access-layer" background="surface-background">
      {children}
    </Box>
  );
}
